import { useClientId } from "@/features/utils/api-scope";
import { useMemo } from "react";
import {
  connectorTypeLabel,
  connectorTypeSpec,
  type Connector,
  type ConnectorType,
} from "../types";
import { useConnectors } from "./use-connectors";

export interface VoiceNumber {
  /** E.164, as configured on the connector. */
  number: string;
  connector_id: string;
  connector_type: ConnectorType;
  /** For a dropdown: the number, with the channel it hangs off. */
  connector_label: string;
}

/**
 * Which types carry telephone numbers of their own is `ownsNumbers` on the type
 * table (features/connectors/types.ts): `web` has none, and `temporary` (a demo)
 * *borrows* the numbers of whoever owns the shared demo line — they are not the
 * tenant's to hand out, which is exactly what a VoIP handset would be claiming
 * by presenting one.
 */
const numbersOf = (connector: Connector): VoiceNumber[] =>
  (connector.configuration?.phone_numbers || [])
    .map((number) => (number || "").trim())
    .filter(Boolean)
    .map((number) => ({
      number,
      connector_id: connector.id,
      connector_type: connector.type,
      connector_label: connectorTypeLabel(connector.type),
    }));

/**
 * Every phone number this tenant owns, across their channels.
 *
 * The one list two different rules are built on, which is why it is a hook
 * rather than a filter written twice:
 *
 *  - a VoIP handset may only present a number the tenant owns — the back-office
 *    offers exactly these, and the backend re-checks it on write *and* on every
 *    outbound call (`services/devices/index.ts`, `isCallerIdOwned`), so a
 *    free-text field could only ever produce a rejection later;
 *  - the handsets themselves are pointless without one, which is what gates the
 *    sidebar entry.
 *
 * Deliberately not filtered on connector status: the backend accepts a caller
 * ID from any connector the tenant holds, and a dropdown that hides a number
 * the API would accept is a support ticket ("my number is not in the list").
 */
export function useVoiceNumbers(clientIdOverride?: string) {
  // `useClientId` is the route param *unless* something above said otherwise —
  // which is what the platform's client view does. Reading `:company` directly
  // here would offer the staff member's own numbers as caller IDs for another
  // client's handset: a list of numbers this client does not own, every one of
  // which the backend then refuses on write.
  const scopedClientId = useClientId();
  // The sidebar renders outside a company route while the preferred company is
  // being restored, and has the id before the router does.
  const clientId = clientIdOverride || scopedClientId;
  const { data: connectors = [], isLoading } = useConnectors(clientId || "");

  const numbers = useMemo(() => {
    const all = connectors
      .filter((connector) => connectorTypeSpec(connector.type)?.ownsNumbers)
      .flatMap(numbersOf);

    // The same number can sit on two connectors (a migration in progress);
    // offering it twice is a choice with no consequence and looks like a bug.
    const seen = new Set<string>();
    return all.filter((entry) => {
      if (seen.has(entry.number)) return false;
      seen.add(entry.number);
      return true;
    });
  }, [connectors]);

  return { numbers, isLoading };
}
